import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, PartyPopper, Search, Pencil } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useDebounce } from '../../hooks/useDebounce';
import { eventTeamApiService } from '../../services/eventTeamApi';
import type {
  EventItem,
  EventItemFull,
  EventItemStatus,
  EventItemType,
  EventProject,
  EventTag,
} from '../../types/eventTeam';
import { EVENT_ITEM_STATUSES, EVENT_ITEM_TYPES } from '../../types/eventTeam';
import { cn } from '../../utils/cn';

export interface EventItemModalPrefill {
  title?: string;
  description?: string;
  category?: string | null;
  event_type?: EventItemType;
  priority?: number;
  project_id?: string | null;
}

export interface EventItemModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  /** Required in edit mode. */
  item?: EventItemFull | EventItem | null;
  /** Optional prefill for create mode (e.g. "Run it again" from the archive). */
  prefill?: EventItemModalPrefill;
  /** Pre-link the new event to a project (create mode). */
  projectId?: string;
  onClose: () => void;
  onSuccess: (item: EventItem) => void;
}

function formatLabel(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** ms epoch → value for `<input type="datetime-local">` in the local timezone. */
function msToLocalInput(ms: number | null | undefined): string {
  if (ms == null) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToMs(value: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Create/edit modal covering every writable `EventItem` field (guide §1.3/§1.4). */
export function EventItemModal({
  isOpen,
  mode,
  item,
  prefill,
  projectId: initialProjectId,
  onClose,
  onSuccess,
}: EventItemModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<EventItemStatus>('idea');
  const [priority, setPriority] = useState(5);
  const [category, setCategory] = useState('');
  const [eventType, setEventType] = useState<EventItemType>('one_off');
  const [scheduledStart, setScheduledStart] = useState('');
  const [scheduledEnd, setScheduledEnd] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [location, setLocation] = useState('');
  const [capacity, setCapacity] = useState('');

  const [selectedProject, setSelectedProject] = useState<EventProject | null>(null);
  const [projectSearch, setProjectSearch] = useState('');
  const debouncedProjectSearch = useDebounce(projectSearch, 300);
  const [projectResults, setProjectResults] = useState<EventProject[]>([]);
  const [isSearchingProjects, setIsSearchingProjects] = useState(false);

  const [tags, setTags] = useState<EventTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSelectedTagIds([]);
    setProjectSearch('');
    setProjectResults([]);

    if (mode === 'edit' && item) {
      setTitle(item.title);
      setDescription(item.description);
      setStatus(item.status);
      setPriority(item.priority);
      setCategory(item.category ?? '');
      setEventType(item.event_type);
      setScheduledStart(msToLocalInput(item.scheduled_start));
      setScheduledEnd(msToLocalInput(item.scheduled_end));
      setDueDate(msToLocalInput(item.due_date));
      setLocation(item.location ?? '');
      setCapacity(item.capacity != null ? String(item.capacity) : '');
    } else {
      setTitle(prefill?.title ?? '');
      setDescription(prefill?.description ?? '');
      setStatus('idea');
      setPriority(prefill?.priority ?? 5);
      setCategory(prefill?.category ?? '');
      setEventType(prefill?.event_type ?? 'one_off');
      setScheduledStart('');
      setScheduledEnd('');
      setDueDate('');
      setLocation('');
      setCapacity('');
      setSelectedProject(null);
    }

    if (mode === 'create') {
      eventTeamApiService.listTags().then(setTags).catch(() => setTags([]));
      const preId = initialProjectId ?? prefill?.project_id;
      if (preId) {
        eventTeamApiService
          .getProject(preId)
          .then((p) => setSelectedProject(p))
          .catch(() => setSelectedProject(null));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || mode !== 'create' || debouncedProjectSearch.trim().length === 0) {
      setProjectResults([]);
      return;
    }
    let cancelled = false;
    setIsSearchingProjects(true);
    eventTeamApiService
      .searchProjects(debouncedProjectSearch)
      .then((res) => {
        if (!cancelled) setProjectResults(res);
      })
      .catch(() => {
        if (!cancelled) setProjectResults([]);
      })
      .finally(() => {
        if (!cancelled) setIsSearchingProjects(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedProjectSearch, isOpen, mode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const startMs = localInputToMs(scheduledStart);
      const endMs = localInputToMs(scheduledEnd);
      if (startMs != null && endMs != null && endMs <= startMs) {
        setError('Scheduled end must be after the scheduled start.');
        setIsSubmitting(false);
        return;
      }
      if (mode === 'create') {
        const created = await eventTeamApiService.createEvent({
          title: title.trim(),
          description: description.trim(),
          status,
          priority,
          category: category.trim() || null,
          event_type: eventType,
          project_id: selectedProject?.id ?? null,
          scheduled_start: startMs,
          scheduled_end: endMs,
          due_date: localInputToMs(dueDate),
          location: location.trim() || null,
          capacity: capacity.trim() !== '' ? Number(capacity) : null,
          tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
        });
        onSuccess(created);
      } else if (item) {
        const updated = await eventTeamApiService.updateEvent(item.id, {
          title: title.trim(),
          description: description.trim(),
          priority,
          category: category.trim() || null,
          event_type: eventType,
          scheduled_start: startMs,
          scheduled_end: endMs,
          due_date: localInputToMs(dueDate),
          location: location.trim() || null,
          capacity: capacity.trim() !== '' ? Number(capacity) : null,
        });
        onSuccess(updated);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save event');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-glass-400 hover:text-white p-2 touch-manipulation"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-4 flex items-center gap-2">
          {mode === 'create' ? (
            <>
              <PartyPopper className="w-6 h-6 text-accent-400" />
              New event
            </>
          ) : (
            <>
              <Pencil className="w-6 h-6 text-accent-400" />
              Edit event
            </>
          )}
        </h2>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="glass-input w-full text-sm"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="glass-input w-full text-sm"
              rows={3}
              required
            />
          </div>

          {mode === 'create' && (
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Project</label>
              {selectedProject ? (
                <div className="flex items-center justify-between p-2 rounded-lg bg-accent-500/10 border border-accent-500/20 gap-2">
                  <span className="text-sm font-medium text-white truncate">{selectedProject.title}</span>
                  <button
                    type="button"
                    onClick={() => setSelectedProject(null)}
                    className="text-glass-400 hover:text-white flex-shrink-0 p-1"
                    aria-label="Clear project"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                  <input
                    type="text"
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    className="glass-input w-full pl-10 text-sm"
                    placeholder="Search projects (optional)..."
                  />
                  {(projectResults.length > 0 || isSearchingProjects) && projectSearch.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 glass-card border border-glass-200/20 rounded-lg overflow-hidden z-10 max-h-48 overflow-y-auto">
                      {isSearchingProjects && (
                        <div className="p-3 flex items-center justify-center">
                          <Loader className="w-4 h-4 animate-spin text-accent-400" />
                        </div>
                      )}
                      {projectResults.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setSelectedProject(p);
                            setProjectSearch('');
                            setProjectResults([]);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-glass-200/10 text-sm text-white"
                        >
                          {p.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {mode === 'create' && (
              <div>
                <label className="text-sm text-glass-300 mb-1 block">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as EventItemStatus)}
                  className="glass-input w-full text-sm"
                >
                  {EVENT_ITEM_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {formatLabel(s)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Priority (1–10)</label>
              <select
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                className="glass-input w-full text-sm"
              >
                {Array.from({ length: 10 }, (_, i) => i + 1).map((p) => (
                  <option key={p} value={p}>
                    P{p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Event type</label>
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value as EventItemType)}
                className="glass-input w-full text-sm"
              >
                {EVENT_ITEM_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {formatLabel(t)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Category</label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="e.g. minigame, seasonal, pvp"
              />
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Capacity</label>
              <input
                type="number"
                min={0}
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="Max players (optional)"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Location</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="e.g. events.playcdu.co spawn arena"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Run window start</label>
              <input
                type="datetime-local"
                value={scheduledStart}
                onChange={(e) => setScheduledStart(e.target.value)}
                className="glass-input w-full text-sm"
              />
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Run window end</label>
              <input
                type="datetime-local"
                value={scheduledEnd}
                onChange={(e) => setScheduledEnd(e.target.value)}
                className="glass-input w-full text-sm"
              />
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Prep due date</label>
              <input
                type="datetime-local"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="glass-input w-full text-sm"
              />
            </div>
          </div>

          {mode === 'create' && tags.length > 0 && (
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Tags</label>
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag) => {
                  const selected = selectedTagIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() =>
                        setSelectedTagIds((prev) =>
                          selected ? prev.filter((id) => id !== tag.id) : [...prev, tag.id],
                        )
                      }
                      className={cn(
                        'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium border transition-all',
                        selected ? 'ring-1 ring-current' : 'opacity-60 hover:opacity-100',
                      )}
                      style={{
                        backgroundColor: `${tag.color}20`,
                        borderColor: `${tag.color}40`,
                        color: tag.color,
                      }}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="glass-button-secondary text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !title.trim()}
              className="glass-button text-sm inline-flex items-center gap-1.5"
            >
              {isSubmitting && <Loader className="w-4 h-4 animate-spin" />}
              {mode === 'create' ? 'Create event' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
